// NEW_FILE_JUSTIFIED: Sub-component for ExtractedLeadsDialog — renders a single extracted lead row with expand/create
import { useCallback, useState } from 'react';
import { useMutation } from 'convex/react';
import { api } from '@convex/_generated/api';
import type { Id } from '@convex/_generated/dataModel';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Loader2,
  ExternalLink,
  UserPlus,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { IExtractedLeadWithStatus } from '@/@types/leads';

interface IExtractedLeadRowProps {
  // LOCAL_TYPE_JUSTIFIED: Internal sub-component props — not reused elsewhere
  lead: IExtractedLeadWithStatus;
  index: number;
  gmailThreadId: string;
  orgServiceTypes?: Array<{ _id: string; orgId: string; name: string; requiredFieldCount: number }>;
  onLeadCreated: (index: number, leadId: string) => void;
}

export function ExtractedLeadRow({
  lead,
  index,
  gmailThreadId,
  orgServiceTypes,
  onLeadCreated,
}: IExtractedLeadRowProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const createLead = useMutation(api.leads.createLead);
  const createServiceData = useMutation(api.leadServiceData.create);

  const { leadData, existingLead } = lead;
  const isExisting = existingLead !== null;

  const handleCreate = useCallback(async () => {
    setIsCreating(true);
    try {
      const leadId = await createLead({
        name: leadData.name,
        phoneNumber: leadData.phoneNumber ?? undefined,
        email: leadData.email ?? undefined,
        source: leadData.source ?? undefined,
        leadType: leadData.leadType,
        temperature: leadData.temperature,
        serviceInterest: leadData.serviceInterest,
        estimatedValue: leadData.estimatedValue,
        gmailThreadId,
      });

      // Create service data entries for each matched service
      if (leadData.matchedServices.length > 0 && orgServiceTypes) {
        for (const matched of leadData.matchedServices) {
          const serviceType = orgServiceTypes.find(st => st.name === matched.serviceTypeName);
          if (!serviceType) continue;

          try {
            const collectedFields = matched.extractedFields.map(f => ({
              fieldLabel: f.fieldLabel,
              value: f.value,
            }));

            // Add default Duration and Provider for car/motorbike insurance
            const insuranceDefaults = ['car insurance', 'motorbike insurance'];
            if (insuranceDefaults.includes(matched.serviceTypeName.toLowerCase())) {
              if (!collectedFields.some(f => f.fieldLabel === 'Duration (months)')) {
                collectedFields.push({ fieldLabel: 'Duration (months)', value: '12' });
              }
              if (!collectedFields.some(f => f.fieldLabel === 'Provider')) {
                collectedFields.push({ fieldLabel: 'Provider', value: 'AIG' });
              }
            }

            await createServiceData({
              leadId,
              orgId: serviceType.orgId as Id<'organisations'>, // AS_JUSTIFIED: orgId from useQuery result typed as string in IExtractedLeadsDialogProps
              serviceTypeId: serviceType._id as Id<'orgServiceTypes'>, // AS_JUSTIFIED: _id from useQuery result typed as string in IExtractedLeadsDialogProps
              collectedFields,
            });
          } catch {
            console.error(`Failed to create service data for ${matched.serviceTypeName}`);
          }
        }
      }

      toast.success(`Lead "${leadData.name}" created`);
      onLeadCreated(index, leadId);
    } catch (error: unknown) {
      const msg =
        error instanceof Error && 'data' in error
          ? String((error as Error & { data: unknown }).data) // AS_JUSTIFIED: ConvexError always has .data field containing the message string
          : undefined;
      if (msg && msg.includes('already exists')) {
        toast.warning(msg);
      } else {
        toast.error(`Failed to create lead "${leadData.name}"`);
      }
    } finally {
      setIsCreating(false);
    }
  }, [
    leadData,
    gmailThreadId,
    orgServiceTypes,
    createLead,
    createServiceData,
    onLeadCreated,
    index,
  ]);

  return (
    <div
      className={cn(
        'rounded-lg border border-border p-3 transition-colors',
        isExisting ? 'bg-muted/30 opacity-70' : 'bg-card'
      )}
    >
      {/* Summary row */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span className="font-medium text-sm truncate">{leadData.name}</span>
          {leadData.email && (
            <span className="text-xs text-muted-foreground truncate hidden sm:inline">
              {leadData.email}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {leadData.matchedServices.slice(0, 2).map((ms, i) => (
            <Badge key={i} variant="outline" className="text-xs hidden sm:inline-flex">
              {ms.serviceTypeName}
            </Badge>
          ))}

          {isExisting ? (
            <Badge variant="secondary" className="text-xs">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Exists: {existingLead.name}
            </Badge>
          ) : (
            <Button
              size="sm"
              variant="default"
              onClick={handleCreate}
              disabled={isCreating}
              className="h-7 text-xs"
            >
              {isCreating ? (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <UserPlus className="w-3 h-3 mr-1" />
              )}
              Create
            </Button>
          )}

          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7"
            onClick={() => setIsExpanded(!isExpanded)}
            aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
          >
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {isExpanded && (
        <div className="mt-3 space-y-2 text-sm">
          <Separator />
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <span className="text-muted-foreground">Phone:</span> {leadData.phoneNumber ?? 'N/A'}
            </div>
            <div>
              <span className="text-muted-foreground">Email:</span> {leadData.email ?? 'N/A'}
            </div>
            {leadData.temperature && (
              <div>
                <span className="text-muted-foreground">Temperature:</span> {leadData.temperature}
              </div>
            )}
            {leadData.source && (
              <div>
                <span className="text-muted-foreground">Source:</span> {leadData.source}
              </div>
            )}
          </div>

          {leadData.notes && (
            <p className="text-xs text-muted-foreground italic">{leadData.notes}</p>
          )}

          {leadData.matchedServices.length > 0 && (
            <div className="space-y-1">
              <span className="text-xs font-medium">Services:</span>
              {leadData.matchedServices.map((ms, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <CheckCircle2 className="w-3 h-3 text-green-500" />
                  <span>{ms.serviceTypeName}</span>
                  <Badge variant="secondary" className="text-[10px] h-4">
                    {ms.confidence}
                  </Badge>
                  <span className="text-muted-foreground">
                    ({ms.extractedFields.length} fields)
                  </span>
                </div>
              ))}
            </div>
          )}

          {isExisting && existingLead && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <ExternalLink className="w-3 h-3" />
              <a href={`/leads/${existingLead.id}`} className="underline underline-offset-2">
                View existing lead
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
